import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffect } from 'react';
import { useAfterIdle } from '@/hooks/useAfterIdle';
import { etagFetch, readEtagCache } from '@/lib/etagFetch';

export interface TypographySetting {
  id: string;
  platform: 'desktop' | 'mobile';
  size_category: 'meta' | 'body' | 'heading';
  font_size: string;
  created_at: string;
  updated_at: string;
}

// Default values (fallback if DB is empty)
const defaultSettings: Omit<TypographySetting, 'id' | 'created_at' | 'updated_at'>[] = [
  { platform: 'desktop', size_category: 'meta', font_size: '16px' },
  { platform: 'desktop', size_category: 'body', font_size: '18px' },
  { platform: 'desktop', size_category: 'heading', font_size: '48px' },
  { platform: 'mobile', size_category: 'meta', font_size: '14px' },
  { platform: 'mobile', size_category: 'body', font_size: '16px' },
  { platform: 'mobile', size_category: 'heading', font_size: '32px' },
];

export const useTypographySettings = () => {
  const isIdle = useAfterIdle();
  return useQuery({
    queryKey: ['typography-settings'],
    queryFn: async () => {
      const fresh = await etagFetch<TypographySetting[]>('typography');
      return (fresh ?? []) as TypographySetting[];
    },
    initialData: () => readEtagCache<TypographySetting[]>('typography') ?? undefined,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes - typography rarely changes
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    // Defer until after first paint — defaults already applied via CSS vars,
    // and DB values rarely change, so this can never block LCP.
    enabled: isIdle,
  });
};

export const useUpdateTypography = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      platform, 
      size_category, 
      font_size 
    }: { 
      platform: 'desktop' | 'mobile'; 
      size_category: 'meta' | 'body' | 'heading'; 
      font_size: string;
    }) => {
      const { data, error } = await supabase
        .from('typography_settings')
        .update({ font_size })
        .eq('platform', platform)
        .eq('size_category', size_category)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['typography-settings'] });
    },
  });
};

// Hook to apply typography settings as CSS variables
export const useApplyTypography = () => {
  const { data: settings } = useTypographySettings();

  useEffect(() => {
    if (!settings || settings.length === 0) {
      // Apply defaults if no settings
      applyTypographyToDOM(defaultSettings as any);
      return;
    }
    applyTypographyToDOM(settings);
  }, [settings]);
};

const applyTypographyToDOM = (settings: TypographySetting[] | Omit<TypographySetting, 'id' | 'created_at' | 'updated_at'>[]) => {
  const root = document.documentElement;
  
  settings.forEach((setting) => {
    const varName = `--font-size-${setting.platform}-${setting.size_category}`;
    root.style.setProperty(varName, setting.font_size);
  });
};

// Helper to get typography class based on category
export const getTypographyClass = (category: 'meta' | 'body' | 'heading'): string => {
  switch (category) {
    case 'meta':
      return 'text-typography-meta';
    case 'body':
      return 'text-typography-body';
    case 'heading':
      return 'text-typography-heading';
    default:
      return 'text-typography-body';
  }
};
